import React, { useState, useMemo } from 'react';
import { useStore } from '../store-hybrid';
import { FundTransaction } from '../types';
import { parseDate } from '../utils/dateUtils';

export const FundPage: React.FC = () => {
  const { fund, cars, suppliers, addFundTransaction, deleteFundTransaction } = useStore();
  const [isAddMoneyModalOpen, setIsAddMoneyModalOpen] = useState(false);
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FundTransaction['category']>('other');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    transaction: FundTransaction | null;
  }>({ isOpen: false, transaction: null });
  
  // PIN protection for Total Benefit
  const [showBenefit, setShowBenefit] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);

  const currentBalance = fund?.currentBalance || 0;

  const handlePinSubmit = () => {
    if (pinInput === '1610') {
      setShowBenefit(true);
      setPinError(false);
      setPinInput('');
    } else {
      setPinError(true);
      setTimeout(() => setPinError(false), 2000);
    }
  };
  
  
  // Memoize transactions to prevent re-renders
  const transactions = useMemo(() => fund?.transactions || [], [fund?.transactions]);

  // Calculate total benefit (net profit from sales + rental revenue)
  const calculatedTotalBenefit = useMemo(() => {
    // Calculate net profit from car sales
    const soldCars = cars.filter(car => car.isSold && car.finalSalePrice && car.purchasePrice);
    const totalSalesProfit = soldCars.reduce((sum, car) => {
      const profit = (car.finalSalePrice || 0) - (car.purchasePrice || 0);
      return sum + profit;
    }, 0);

    // Calculate total rental revenue
    const rentalTransactions = transactions.filter(t => t.category === 'rental' && t.type === 'add_money');
    const totalRentalRevenue = rentalTransactions.reduce((sum, t) => sum + t.amount, 0);

    return totalSalesProfit + totalRentalRevenue;
  }, [cars, transactions]);

  // Calculate total charges (excluding décaissement)
  const calculatedTotalCharges = useMemo(() => {
    const chargeTransactions = transactions.filter(t => 
      t.type === 'add_charge' && t.category !== 'decaissement'
    );
    return chargeTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [transactions]);

  // Use manual values if set, otherwise use calculated
  const totalBenefit = fund?.manualTotalBenefit ?? calculatedTotalBenefit;
  const totalCharges = fund?.manualTotalCharges ?? calculatedTotalCharges;

  // Sort transactions by date (newest first)
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => 
      parseDate(b.timestamp).getTime() - parseDate(a.timestamp).getTime()
    );
  }, [transactions]);

  const handleAddMoney = async () => {
    if (!amount || !description.trim()) return;
    
    setIsLoading(true);
    try {
      await addFundTransaction({
        type: 'add_money',
        amount: parseFloat(amount),
        description: description.trim(),
        category,
        supplierId: category === 'supplier' && selectedSupplierId ? selectedSupplierId : undefined
      });
      
      // Reset form
      setAmount('');
      setDescription('');
      setCategory('other');
      setSelectedSupplierId('');
      setIsAddMoneyModalOpen(false);
    } catch (error) {
      console.error('Error adding money:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCharge = async () => {
    if (!amount || !description.trim()) return;
    
    setIsLoading(true);
    try {
      await addFundTransaction({
        type: 'add_charge',
        amount: parseFloat(amount),
        description: description.trim(),
        category,
        supplierId: category === 'supplier' && selectedSupplierId ? selectedSupplierId : undefined
      });
      
      // Reset form
      setAmount('');
      setDescription('');
      setCategory('other');
      setSelectedSupplierId('');
      setIsAddChargeModalOpen(false);
    } catch (error) {
      console.error('Error adding charge:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!deleteConfirmModal.transaction) return;
    
    setIsLoading(true);
    try {
      await deleteFundTransaction(deleteConfirmModal.transaction.id);
      setDeleteConfirmModal({ isOpen: false, transaction: null });
    } catch (error) {
      console.error('Error deleting transaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTransactionIcon = (type: FundTransaction['type']) => {
    return type === 'add_money' ? '💰' : '💸';
  };

  const getTransactionColor = (type: FundTransaction['type']) => {
    return type === 'add_money' 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400';
  };

  const getCategoryIcon = (category: FundTransaction['category']) => {
    switch (category) {
      case 'fuel': return '⛽';
      case 'maintenance': return '🔧';
      case 'insurance': return '🛡️';
      case 'deposit': return '🏦';
      case 'expense': return '💸';
      case 'income': return '💰';
      case 'rental': return '🚗';
      case 'sale': return '🏷️';
      case 'supplier': return '🏢';
      case 'benefit_rental': return '🎯';
      case 'benefit_sale': return '🎯';
      case 'decaissement': return '💳';
      default: return '📋';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">💼 Fund Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Track your business finances and manage transactions
        </p>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Current Balance Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current Balance</h2>
            <div className={`text-4xl font-bold ${currentBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatCurrency(currentBalance)}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Available funds in account
            </p>
          </div>
        </div>

        {/* Total Benefit Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Total Benefit</h2>
              {fund?.manualTotalBenefit !== undefined && (
                <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-full">
                  ✏️ Manual
                </span>
              )}
            </div>
            {showBenefit ? (
              <>
                <div className={`text-4xl font-bold ${totalBenefit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(totalBenefit)}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {fund?.manualTotalBenefit !== undefined ? 'Manually set value' : 'Sales profit + rental revenue'}
                </p>
                <button
                  onClick={() => setShowBenefit(false)}
                  className="mt-3 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  🔒 Lock
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="text-6xl">🔒</div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter PIN to view
                </p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
                    placeholder="Enter PIN"
                    maxLength={4}
                    className={`w-24 px-3 py-2 text-center border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      pinError ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  <button
                    onClick={handlePinSubmit}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    ✓
                  </button>
                </div>
                {pinError && (
                  <p className="text-xs text-red-500 mt-1">
                    Incorrect PIN
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Total Charges Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Total Charges</h2>
              {fund?.manualTotalCharges !== undefined && (
                <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-full">
                  ✏️ Manual
                </span>
              )}
            </div>
            <div className="text-4xl font-bold text-red-600 dark:text-red-400">
              {formatCurrency(totalCharges)}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {fund?.manualTotalCharges !== undefined ? 'Manually set value' : 'All expenses and charges'}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="text-center mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {fund?.lastUpdated ? `Last updated: ${new Date(fund.lastUpdated).toLocaleDateString()}` : 'No transactions yet'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => setIsAddMoneyModalOpen(true)}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            💰 Add Money
          </button>
          <button
            onClick={() => setIsAddChargeModalOpen(true)}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            💸 Add Charge
          </button>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Transaction History</h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{transactions.length} transactions</p>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {sortedTransactions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No transactions yet</h3>
              <p className="text-gray-600 dark:text-gray-400">Start by adding money or recording a charge</p>
            </div>
          ) : (
            sortedTransactions.map((transaction) => (
              <div key={transaction.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </h4>
                        <span className="text-sm">
                          {getCategoryIcon(transaction.category)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {parseDate(transaction.timestamp).toLocaleString()}
                        </p>
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-gray-600 dark:text-gray-400 capitalize">
                          {transaction.category === 'benefit_rental' ? 'Benefit Location' : 
                           transaction.category === 'benefit_sale' ? 'Benefit Vente' :
                           transaction.category === 'decaissement' ? 'Décaissement' :
                           transaction.category?.replace('_', ' ') || 'other'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`text-lg font-semibold ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'add_money' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </div>
                    <button
                      onClick={() => setDeleteConfirmModal({ isOpen: true, transaction })}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete transaction"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Money Modal */}
      {isAddMoneyModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">💰 Add Money</h3>
                <button
                  onClick={() => setIsAddMoneyModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Initial deposit, Rental payment..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as FundTransaction['category'])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="income">💰 Income</option>
                    <option value="rental">🚗 Rental Payment</option>
                    <option value="sale">🏷️ Car Sale</option>
                    <option value="benefit_rental">🎯 Benefit Location</option>
                    <option value="benefit_sale">🎯 Benefit Vente</option>
                    <option value="supplier">🏢 Supplier Payment</option>
                    <option value="deposit">🏦 Deposit</option>
                    <option value="other">📋 Other</option>
                  </select>
                </div>

                {/* Info message for benefit categories */}
                {(category === 'benefit_rental' || category === 'benefit_sale') && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>ℹ️ Info:</strong> This amount will be added to both <strong>Current Balance</strong> and <strong>Total Benefit</strong>.
                    </p>
                  </div>
                )}

                {/* Supplier Selection (only show when supplier category is selected) */}
                {category === 'supplier' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Supplier
                    </label>
                    <select
                      value={selectedSupplierId}
                      onChange={(e) => setSelectedSupplierId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="">Select a supplier...</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} (Balance: {formatCurrency(supplier.balance || 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsAddMoneyModalOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMoney}
                  disabled={!amount || !description.trim() || isLoading}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  {isLoading ? 'Adding...' : 'Add Money'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Charge Modal */}
      {isAddChargeModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">💸 Add Charge</h3>
                <button
                  onClick={() => setIsAddChargeModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Fuel, Maintenance, Insurance..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as FundTransaction['category'])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="fuel">⛽ Fuel</option>
                    <option value="maintenance">🔧 Maintenance</option>
                    <option value="insurance">🛡️ Insurance</option>
                    <option value="supplier">🏢 Supplier Payment</option>
                    <option value="expense">💸 Expense</option>
                    <option value="decaissement">💳 Décaissement</option>
                    <option value="other">📋 Other</option>
                  </select>
                </div>

                {/* Info message for décaissement category */}
                {category === 'decaissement' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <strong>ℹ️ Info:</strong> This amount will <strong>only decrease Current Balance</strong>. It will <strong>not be added</strong> to Total Charges.
                    </p>
                  </div>
                )}

                {/* Supplier Selection (only show when supplier category is selected) */}
                {category === 'supplier' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Supplier
                    </label>
                    <select
                      value={selectedSupplierId}
                      onChange={(e) => setSelectedSupplierId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="">Select a supplier...</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} (Balance: {formatCurrency(supplier.balance || 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsAddChargeModalOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCharge}
                  disabled={!amount || !description.trim() || isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  {isLoading ? 'Adding...' : 'Add Charge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isOpen && deleteConfirmModal.transaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">🗑️ Delete Transaction</h3>
                <button
                  onClick={() => setDeleteConfirmModal({ isOpen: false, transaction: null })}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      {getTransactionIcon(deleteConfirmModal.transaction.type)}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {deleteConfirmModal.transaction.description}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`font-semibold ${getTransactionColor(deleteConfirmModal.transaction.type)}`}>
                          {deleteConfirmModal.transaction.type === 'add_money' ? '+' : '-'}{formatCurrency(deleteConfirmModal.transaction.amount)}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          • {parseDate(deleteConfirmModal.transaction.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete this transaction? This will adjust your balance and cannot be undone.
                </p>
                
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Balance Impact:</strong> {deleteConfirmModal.transaction.type === 'add_money' 
                      ? `Your balance will decrease by ${formatCurrency(deleteConfirmModal.transaction.amount)}`
                      : `Your balance will increase by ${formatCurrency(deleteConfirmModal.transaction.amount)}`
                    }
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmModal({ isOpen: false, transaction: null })}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTransaction}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
